/**
 * useGeneratePdf Hook (Story 5.12, Task 4.1)
 *
 * TanStack mutation for POST /api/reports/:id/generate-pdf.
 * Manages PDF generation lifecycle: idle → generating → success/error.
 *
 * AC #1: Progress feedback during generation.
 * AC #4: Download via GET /api/reports/:id/pdf after success.
 * AC #8: Button state transitions.
 */

import { useMutation } from "@tanstack/react-query";
import { apiPost, apiGet } from "../lib/apiClient";
import { useCallback, useState } from "react";

// ============================================================================
// Types
// ============================================================================

export interface GeneratePdfResponse {
  pdfUrl: string;
  status: "completed";
  sizeBytes: number;
  pageCount: number;
}

export type PdfStatus = "idle" | "generating" | "completed" | "failed";

export interface UseGeneratePdfResult {
  /** Trigger PDF generation */
  generatePdf: (reportId: string) => void;
  /** Download the generated PDF */
  downloadPdf: (reportId: string) => void;
  /** Current status of PDF generation */
  status: PdfStatus;
  /** Whether generation is in progress */
  isGenerating: boolean;
  /** Whether PDF is available for download */
  isCompleted: boolean;
  /** Error message if generation failed */
  error: string | null;
  /** Generation result data */
  data: GeneratePdfResponse | null;
  /** Reset state back to idle */
  reset: () => void;
}

// ============================================================================
// Hook
// ============================================================================

export function useGeneratePdf(): UseGeneratePdfResult {
  const [status, setStatus] = useState<PdfStatus>("idle");

  const mutation = useMutation({
    mutationFn: async (reportId: string): Promise<GeneratePdfResponse> => {
      setStatus("generating");

      const response = await apiPost(
        `/api/reports/${reportId}/generate-pdf`
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        const detail =
          (errorBody as Record<string, string> | null)?.detail ??
          `PDF generation failed (${response.status})`;
        throw new Error(detail);
      }

      const json = (await response.json()) as { data: GeneratePdfResponse };
      return json.data;
    },
    onSuccess: () => {
      setStatus("completed");
    },
    onError: () => {
      setStatus("failed");
    },
  });

  const generatePdf = useCallback(
    (reportId: string) => {
      mutation.mutate(reportId);
    },
    [mutation]
  );

  const downloadPdf = useCallback((reportId: string) => {
    // Open the authenticated download endpoint in a new tab
    // We use apiGet and create an object URL for the download
    (async () => {
      try {
        const response = await apiGet(`/api/reports/${reportId}/pdf`);
        if (!response.ok) {
          throw new Error("Download failed");
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        // Extract filename from Content-Disposition header if available
        const disposition = response.headers.get("Content-Disposition");
        const filenameMatch = disposition?.match(/filename="(.+?)"/);
        const filename = filenameMatch?.[1] ?? "report.pdf";

        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch {
        // Silently fail — toast notification handled by caller
      }
    })();
  }, []);

  const reset = useCallback(() => {
    setStatus("idle");
    mutation.reset();
  }, [mutation]);

  return {
    generatePdf,
    downloadPdf,
    status,
    isGenerating: status === "generating",
    isCompleted: status === "completed",
    error: mutation.error?.message ?? null,
    data: mutation.data ?? null,
    reset,
  };
}
